import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api, { announcementAPI } from '../services/api';
import { 
  Search, Briefcase, Settings, BookOpen, FileText, 
  Sparkles, Layers, Plus, ArrowRight, User, Star, TrendingUp,
  Zap, Users, ShoppingBag, CreditCard, Receipt, Package,
  Bell, Shield, Crown, Globe, Wallet, ClipboardList, PieChart,
  UserPlus, FolderOpen, Store, Layout, ChevronRight, Eye,
  Rocket, MessageCircle, CheckCircle2,
  ChevronLeft, Play, Pause, Megaphone,
  Lightbulb, History, X
} from 'lucide-react';

// ============================================================
// ANIMATIONS CSS GLOBALES
// ============================================================
const GlobalStyles = () => (
  <style>{`
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-12px); }
    }
    @keyframes float-delayed {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      33% { transform: translateY(-8px) rotate(2deg); }
      66% { transform: translateY(-4px) rotate(-1deg); }
    }
    @keyframes shimmer {
      0% { transform: translateX(-100%) skewX(-15deg); }
      100% { transform: translateX(200%) skewX(-15deg); }
    }
    @keyframes gradient-x {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    @keyframes fade-in-up {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fade-in-scale {
      from { opacity: 0; transform: scale(0.9); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes slide-in-right {
      from { opacity: 0; transform: translateX(30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes pulse-soft {
      0%, 100% { opacity: 0.4; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(1.05); }
    }
    @keyframes bounce-subtle {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
    }
    @keyframes progress-bar {
      from { width: 0%; }
      to { width: 100%; }
    }
    @keyframes dot-pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.5); opacity: 0.5; }
    }
    .animate-float { animation: float 6s ease-in-out infinite; }
    .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
    .animate-gradient-x { 
      background-size: 200% 200%; 
      animation: gradient-x 4s ease infinite; 
    }
    .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
    .animate-fade-in-scale { animation: fade-in-scale 0.5s ease-out forwards; }
    .animate-slide-in-right { animation: slide-in-right 0.5s ease-out forwards; }
    .animate-pulse-soft { animation: pulse-soft 4s ease-in-out infinite; }
    .animate-bounce-subtle { animation: bounce-subtle 2s ease-in-out infinite; }
    .group-shimmer:hover .shimmer-effect {
      animation: shimmer 0.8s ease-in-out;
    }
    .scrollbar-hide::-webkit-scrollbar { display: none; }
    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    .stagger-1 { animation-delay: 0.05s; }
    .stagger-2 { animation-delay: 0.1s; }
    .stagger-3 { animation-delay: 0.15s; }
    .stagger-4 { animation-delay: 0.2s; }
    .stagger-5 { animation-delay: 0.25s; }
    .stagger-6 { animation-delay: 0.3s; }
  `}</style>
);

// ============================================================
// COMPOSANT PRINCIPAL - PAGE D'ACCUEIL DJAMANN
// Hub central de navigation - Design moderne et intuitif
// ============================================================

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  const [spotlightPaused, setSpotlightPaused] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const searchRef = useRef(null);
  const [recentPages, setRecentPages] = useState([]);
  const [tipDismissed, setTipDismissed] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    try { return JSON.parse(localStorage.getItem('djamann_dismissed_announcements') || '[]'); } catch { return []; }
  });

  // Déterminer le rôle
  const isAuthor = user?.role === 'author' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  // Animate hero on mount
  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Date formatée
  const formattedDate = useMemo(() => {
    const now = new Date();
    const str = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, []);

  // ============================================================
  // DONNÉES : Toutes les sections de l'application
  // ============================================================

  const allSections = useMemo(() => {
    const sections = [
      // --- EXPLORER ---
      {
        id: 'feed',
        title: 'Opportunités',
        description: 'Projets et offres adaptés à votre profil',
        icon: Sparkles,
        path: '/feed',
        gradient: 'from-rose-500 to-pink-600',
        bgLight: 'bg-rose-50',
        iconColor: 'text-rose-600',
        category: 'explorer',
        priority: 1,
        keywords: ['opportunités', 'offres', 'emploi', 'projets', 'missions', 'feed'],
      },
      {
        id: 'services',
        title: 'Services Pro',
        description: 'Trouvez des freelances et professionnels vérifiés',
        icon: Briefcase,
        path: '/services',
        gradient: 'from-blue-500 to-cyan-600',
        bgLight: 'bg-blue-50',
        iconColor: 'text-blue-600',
        category: 'explorer',
        priority: 2,
        keywords: ['services', 'freelance', 'professionnel', 'prestataire'],
      },
      {
        id: 'boutiques',
        title: 'Boutiques',
        description: 'Shopping et marketplace en ligne',
        icon: ShoppingBag,
        path: '/boutiques',
        gradient: 'from-emerald-500 to-teal-600',
        bgLight: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        category: 'explorer',
        priority: 3,
        keywords: ['boutique', 'shop', 'achat', 'marketplace', 'e-commerce', 'shopping'],
      },
      {
        id: 'projects-list',
        title: 'Projets',
        description: 'Parcourez tous les projets collaboratifs',
        icon: BookOpen,
        path: '/projects',
        gradient: 'from-purple-500 to-indigo-600',
        bgLight: 'bg-purple-50',
        iconColor: 'text-purple-600',
        category: 'explorer',
        priority: 4,
        keywords: ['projets', 'collaboration', 'équipe', 'parcourir'],
      },

      // --- MES ACTIVITÉS ---
      {
        id: 'my-applications',
        title: 'Mes Candidatures',
        description: 'Suivez l\'état de vos candidatures',
        icon: ClipboardList,
        path: '/my-applications',
        gradient: 'from-green-500 to-emerald-600',
        bgLight: 'bg-green-50',
        iconColor: 'text-green-600',
        category: 'activites',
        priority: 5,
        keywords: ['candidatures', 'postulations', 'suivi', 'applications'],
      },
      {
        id: 'my-service-requests',
        title: 'Mes Demandes',
        description: 'Gérez vos demandes de services',
        icon: FileText,
        path: '/my-service-requests',
        gradient: 'from-amber-500 to-orange-600',
        bgLight: 'bg-amber-50',
        iconColor: 'text-amber-600',
        category: 'activites',
        priority: 6,
        keywords: ['demandes', 'services', 'requêtes'],
      },
      {
        id: 'notifications',
        title: 'Notifications',
        description: 'Vos alertes et messages importants',
        icon: Bell,
        path: '/notifications',
        gradient: 'from-red-500 to-rose-600',
        bgLight: 'bg-red-50',
        iconColor: 'text-red-600',
        category: 'activites',
        priority: 7,
        keywords: ['notifications', 'alertes', 'messages'],
      },
      {
        id: 'order-tracking',
        title: 'Suivi Commande',
        description: 'Suivez vos commandes en cours',
        icon: Package,
        path: '/commande',
        gradient: 'from-sky-500 to-blue-600',
        bgLight: 'bg-sky-50',
        iconColor: 'text-sky-600',
        category: 'activites',
        priority: 8,
        keywords: ['commande', 'livraison', 'suivi', 'tracking'],
      },

      // --- COMPTE ---
      {
        id: 'profile',
        title: 'Mon Profil',
        description: 'Gérez votre identité et votre visibilité',
        icon: User,
        path: '/profile',
        gradient: 'from-indigo-500 to-violet-600',
        bgLight: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        category: 'compte',
        priority: 9,
        keywords: ['profil', 'identité', 'photo', 'bio'],
      },
      {
        id: 'settings',
        title: 'Paramètres',
        description: 'Configuration de votre compte',
        icon: Settings,
        path: '/settings',
        gradient: 'from-gray-500 to-slate-600',
        bgLight: 'bg-gray-50',
        iconColor: 'text-gray-600',
        category: 'compte',
        priority: 10,
        keywords: ['paramètres', 'configuration', 'réglages', 'compte'],
      },
      {
        id: 'guide',
        title: 'Guide',
        description: 'Apprenez à utiliser DJAMANN efficacement',
        icon: BookOpen,
        path: '/guide',
        gradient: 'from-teal-500 to-cyan-600',
        bgLight: 'bg-teal-50',
        iconColor: 'text-teal-600',
        category: 'compte',
        priority: 11,
        keywords: ['guide', 'aide', 'tutoriel', 'comment'],
      },
    ];

    // Sections auteur/admin
    if (isAuthor) {
      sections.push(
        // --- BUSINESS / WORKSPACE ---
        {
          id: 'workspace',
          title: 'Mon Workspace',
          description: 'Tableau de bord de votre activité business',
          icon: Layout,
          path: '/workspace',
          gradient: 'from-violet-500 to-purple-600',
          bgLight: 'bg-violet-50',
          iconColor: 'text-violet-600',
          category: 'business',
          priority: 1,
          featured: true,
          keywords: ['workspace', 'tableau de bord', 'dashboard', 'business'],
        },
        {
          id: 'create-project',
          title: 'Créer un Projet',
          description: 'Lancez un nouveau projet collaboratif',
          icon: Plus,
          path: '/create-project',
          gradient: 'from-cyan-500 to-blue-600',
          bgLight: 'bg-cyan-50',
          iconColor: 'text-cyan-600',
          category: 'business',
          priority: 2,
          keywords: ['créer', 'projet', 'nouveau', 'lancer'],
        },
        {
          id: 'my-services',
          title: 'Mes Services',
          description: 'Gérez et mettez en avant vos services',
          icon: Star,
          path: '/my-services',
          gradient: 'from-amber-500 to-yellow-600',
          bgLight: 'bg-amber-50',
          iconColor: 'text-amber-600',
          category: 'business',
          priority: 3,
          keywords: ['mes services', 'portfolio', 'offres'],
        },
        {
          id: 'received-applications',
          title: 'Candidatures Reçues',
          description: 'Examinez les candidatures pour vos projets',
          icon: UserPlus,
          path: '/received-applications',
          gradient: 'from-lime-500 to-green-600',
          bgLight: 'bg-lime-50',
          iconColor: 'text-lime-600',
          category: 'business',
          priority: 4,
          keywords: ['candidatures reçues', 'recrutement', 'postulants'],
        },
        {
          id: 'pro-service-requests',
          title: 'Demandes Reçues',
          description: 'Demandes de services de vos clients',
          icon: MessageCircle,
          path: '/pro/service-requests',
          gradient: 'from-orange-500 to-red-600',
          bgLight: 'bg-orange-50',
          iconColor: 'text-orange-600',
          category: 'business',
          priority: 5,
          keywords: ['demandes reçues', 'clients', 'prestation'],
        },

        // --- FINANCES ---
        {
          id: 'workspace-invoices',
          title: 'Factures',
          description: 'Créez et gérez vos factures',
          icon: Receipt,
          path: '/workspace/invoices',
          gradient: 'from-blue-500 to-indigo-600',
          bgLight: 'bg-blue-50',
          iconColor: 'text-blue-600',
          category: 'finances',
          priority: 1,
          keywords: ['factures', 'facturation', 'invoices'],
        },
        {
          id: 'workspace-quotes',
          title: 'Devis',
          description: 'Rédigez et envoyez des devis professionnels',
          icon: FileText,
          path: '/workspace/quotes',
          gradient: 'from-indigo-500 to-blue-600',
          bgLight: 'bg-indigo-50',
          iconColor: 'text-indigo-600',
          category: 'finances',
          priority: 2,
          keywords: ['devis', 'estimation', 'quotes'],
        },
        {
          id: 'workspace-payments',
          title: 'Paiements',
          description: 'Suivi des encaissements et paiements',
          icon: CreditCard,
          path: '/workspace/payments',
          gradient: 'from-green-500 to-emerald-600',
          bgLight: 'bg-green-50',
          iconColor: 'text-green-600',
          category: 'finances',
          priority: 3,
          keywords: ['paiements', 'encaissements', 'argent'],
        },
        {
          id: 'workspace-expenses',
          title: 'Dépenses',
          description: 'Enregistrez et suivez vos dépenses',
          icon: TrendingUp,
          path: '/workspace/expenses',
          gradient: 'from-red-500 to-rose-600',
          bgLight: 'bg-red-50',
          iconColor: 'text-red-600',
          category: 'finances',
          priority: 4,
          keywords: ['dépenses', 'charges', 'frais'],
        },
        {
          id: 'workspace-wallet',
          title: 'Portefeuille',
          description: 'Votre solde et transactions sur la plateforme',
          icon: Wallet,
          path: '/workspace/wallet',
          gradient: 'from-emerald-500 to-teal-600',
          bgLight: 'bg-emerald-50',
          iconColor: 'text-emerald-600',
          category: 'finances',
          priority: 5,
          keywords: ['portefeuille', 'wallet', 'solde', 'transactions'],
        },
        {
          id: 'workspace-report',
          title: 'Rapports',
          description: 'Analyses et statistiques de votre activité',
          icon: PieChart,
          path: '/workspace/report',
          gradient: 'from-purple-500 to-violet-600',
          bgLight: 'bg-purple-50',
          iconColor: 'text-purple-600',
          category: 'finances',
          priority: 6,
          keywords: ['rapports', 'statistiques', 'analyses', 'bilan'],
        },

        // --- GESTION ---
        {
          id: 'workspace-clients',
          title: 'Clients',
          description: 'Gérez votre base de clients',
          icon: Users,
          path: '/workspace/clients',
          gradient: 'from-sky-500 to-blue-600',
          bgLight: 'bg-sky-50',
          iconColor: 'text-sky-600',
          category: 'gestion',
          priority: 1,
          keywords: ['clients', 'contacts', 'carnet'],
        },
        {
          id: 'workspace-products',
          title: 'Produits',
          description: 'Catalogue de vos produits et articles',
          icon: Package,
          path: '/workspace/products',
          gradient: 'from-orange-500 to-amber-600',
          bgLight: 'bg-orange-50',
          iconColor: 'text-orange-600',
          category: 'gestion',
          priority: 2,
          keywords: ['produits', 'articles', 'catalogue', 'stock'],
        },
        {
          id: 'workspace-prestations',
          title: 'Prestations',
          description: 'Suivez vos prestations en cours',
          icon: CheckCircle2,
          path: '/workspace/prestations',
          gradient: 'from-teal-500 to-cyan-600',
          bgLight: 'bg-teal-50',
          iconColor: 'text-teal-600',
          category: 'gestion',
          priority: 3,
          keywords: ['prestations', 'missions', 'tâches'],
        },
        {
          id: 'workspace-shop',
          title: 'Ma Boutique',
          description: 'Gérez votre boutique en ligne',
          icon: Store,
          path: '/workspace/shop',
          gradient: 'from-pink-500 to-rose-600',
          bgLight: 'bg-pink-50',
          iconColor: 'text-pink-600',
          category: 'gestion',
          priority: 4,
          keywords: ['boutique', 'shop', 'vente', 'e-commerce'],
        },
        {
          id: 'workspace-team',
          title: 'Mon Équipe',
          description: 'Gérez les membres de votre équipe',
          icon: Users,
          path: '/workspace/team',
          gradient: 'from-fuchsia-500 to-purple-600',
          bgLight: 'bg-fuchsia-50',
          iconColor: 'text-fuchsia-600',
          category: 'gestion',
          priority: 5,
          keywords: ['équipe', 'team', 'membres', 'collaborateurs'],
        },
        {
          id: 'showcase',
          title: 'Site Vitrine',
          description: 'Créez votre site web professionnel',
          icon: Globe,
          path: '/showcase',
          gradient: 'from-violet-500 to-indigo-600',
          bgLight: 'bg-violet-50',
          iconColor: 'text-violet-600',
          category: 'gestion',
          priority: 6,
          keywords: ['site', 'vitrine', 'web', 'showcase', 'landing'],
        },
        {
          id: 'subscription',
          title: 'Abonnement',
          description: 'Gérez votre plan et vos avantages',
          icon: Crown,
          path: '/my-subscription',
          gradient: 'from-yellow-500 to-amber-600',
          bgLight: 'bg-yellow-50',
          iconColor: 'text-yellow-600',
          category: 'compte',
          priority: 8,
          keywords: ['abonnement', 'plan', 'premium', 'subscription'],
        },
      );
    }

    // Sections admin
    if (isAdmin) {
      sections.push(
        {
          id: 'admin-dashboard',
          title: 'Admin',
          description: 'Tableau de bord d\'administration',
          icon: Shield,
          path: '/admin',
          gradient: 'from-red-600 to-rose-700',
          bgLight: 'bg-red-50',
          iconColor: 'text-red-600',
          category: 'admin',
          priority: 1,
          keywords: ['admin', 'administration', 'gestion plateforme'],
        },
        {
          id: 'admin-accounts',
          title: 'Gestion Comptes',
          description: 'Gérer les comptes utilisateurs',
          icon: Users,
          path: '/admin/account-management',
          gradient: 'from-slate-600 to-gray-700',
          bgLight: 'bg-slate-50',
          iconColor: 'text-slate-600',
          category: 'admin',
          priority: 2,
          keywords: ['comptes', 'utilisateurs', 'modération'],
        },
        {
          id: 'admin-payments',
          title: 'Paiements Plateforme',
          description: 'Suivi des transactions de la plateforme',
          icon: CreditCard,
          path: '/admin/platform-payments',
          gradient: 'from-emerald-600 to-green-700',
          bgLight: 'bg-emerald-50',
          iconColor: 'text-emerald-600',
          category: 'admin',
          priority: 3,
          keywords: ['paiements plateforme', 'transactions'],
        },
      );
    }

    return sections;
  }, [isAuthor, isAdmin]);

  // ============================================================
  // CATÉGORIES
  // ============================================================

  const categories = useMemo(() => {
    const cats = [
      { id: 'all', label: 'Tout', icon: Layers, color: 'text-indigo-600', bg: 'bg-indigo-50' },
      { id: 'explorer', label: 'Explorer', icon: Search, color: 'text-rose-600', bg: 'bg-rose-50' },
      { id: 'activites', label: 'Activités', icon: Zap, color: 'text-amber-600', bg: 'bg-amber-50' },
    ];

    if (isAuthor) {
      cats.push(
        { id: 'business', label: 'Business', icon: Briefcase, color: 'text-violet-600', bg: 'bg-violet-50' },
        { id: 'finances', label: 'Finances', icon: CreditCard, color: 'text-green-600', bg: 'bg-green-50' },
        { id: 'gestion', label: 'Gestion', icon: FolderOpen, color: 'text-orange-600', bg: 'bg-orange-50' },
      );
    }

    cats.push(
      { id: 'compte', label: 'Compte', icon: User, color: 'text-gray-600', bg: 'bg-gray-50' },
    );

    if (isAdmin) {
      cats.push(
        { id: 'admin', label: 'Admin', icon: Shield, color: 'text-red-600', bg: 'bg-red-50' },
      );
    }

    return cats;
  }, [isAuthor, isAdmin]);

  // ============================================================
  // FILTRAGE
  // ============================================================

  const filteredSections = useMemo(() => {
    let items = allSections;

    // Filtrer par catégorie
    if (activeCategory !== 'all') {
      items = items.filter(s => s.category === activeCategory);
    }

    // Filtrer par recherche
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      items = items.filter(s =>
        s.title.toLowerCase().includes(term) ||
        s.description.toLowerCase().includes(term) ||
        s.keywords.some(k => k.includes(term))
      );
    }

    // Trier par priorité
    return items.sort((a, b) => a.priority - b.priority);
  }, [allSections, activeCategory, searchTerm]);

  // ============================================================
  // ACTIONS RAPIDES
  // ============================================================

  const quickActions = useMemo(() => {
    const actions = [
      { label: 'Opportunités', icon: Sparkles, path: '/feed', color: 'from-rose-500 to-pink-600' },
      { label: 'Services', icon: Briefcase, path: '/services', color: 'from-blue-500 to-cyan-600' },
      { label: 'Boutiques', icon: ShoppingBag, path: '/boutiques', color: 'from-emerald-500 to-teal-600' },
    ];

    if (isAuthor) {
      actions.push(
        { label: 'Workspace', icon: Layout, path: '/workspace', color: 'from-violet-500 to-purple-600' },
         { label: 'Site Vitrine', icon: Globe, path: '/showcase', color: 'from-emerald-500 to-green-600' }, { label: 'Nouveau Projet', icon: Plus, path: '/create-project', color: 'from-cyan-500 to-blue-600' },
        { label: 'Ma Boutique', icon: Store, path: '/workspace/shop', color: 'from-pink-500 to-rose-600' },
      
      );
    }

    return actions;
  }, [isAuthor]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleNavigate = useCallback((path) => {
    // Sauvegarder dans l'historique récent
    try {
      const stored = JSON.parse(localStorage.getItem('djamann_recent_pages') || '[]');
      const updated = [path, ...stored.filter(p => p !== path)].slice(0, 10);
      localStorage.setItem('djamann_recent_pages', JSON.stringify(updated));
      setRecentPages(updated.slice(0, 6));
    } catch {}
    navigate(path);
  }, [navigate]);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchTerm.trim() && filteredSections.length > 0) {
      handleNavigate(filteredSections[0].path);
    }
  }, [searchTerm, filteredSections, handleNavigate]);

  // Keyboard shortcut pour la recherche
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSearchTerm('');
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Charger les annonces admin
  useEffect(() => {
    if (!user) return;
    const loadAnnouncements = async () => {
      try {
        const res = await announcementAPI.getUnread();
        if (res.data && Array.isArray(res.data)) {
          setAnnouncements(res.data.filter(a => a && a.id && a.titre && a.message));
        }
      } catch {}
    };
    loadAnnouncements();

    // Vérifier l'expiration de l'abonnement
    const checkSubscriptionExpiration = async () => {
      try {
        await api.post('/subscriptions/check-expiration');
      } catch (err) {
        console.error('Check expiration error:', err.response?.data || err.message);
      }
    };
    checkSubscriptionExpiration();
  }, [user]);

  // Charger les pages récemment visitées depuis localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('djamann_recent_pages') || '[]');
      setRecentPages(stored.slice(0, 6));
    } catch { setRecentPages([]); }
  }, []);

  // ============================================================
  // SPOTLIGHT SLIDES (featured sections slider)
  // ============================================================

  const spotlightSlides = useMemo(() => [
    {
      id: 'opportunities',
      title: 'Découvrez les Opportunités',
      subtitle: 'Projets exclusifs et offres adaptées à votre profil',
      icon: Sparkles,
      gradient: 'from-rose-500 via-pink-500 to-fuchsia-600',
      path: '/feed',
      cta: 'Explorer',
      stat: '2,400+',
      statLabel: 'offres actives',
    },
    {
      id: 'services',
      title: 'Services Professionnels',
      subtitle: 'Trouvez des freelances et experts vérifiés',
      icon: Briefcase,
      gradient: 'from-blue-500 via-cyan-500 to-teal-500',
      path: '/services',
      cta: 'Découvrir',
      stat: '1,800+',
      statLabel: 'services disponibles',
    },
    {
      id: 'marketplace',
      title: 'Marketplace & Boutiques',
      subtitle: 'Shopping en ligne nouvelle génération',
      icon: ShoppingBag,
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
      path: '/boutiques',
      cta: 'Visiter',
      stat: '500+',
      statLabel: 'boutiques partenaires',
    },
    ...(isAuthor ? [{
      id: 'workspace',
      title: 'Votre Workspace Pro',
      subtitle: 'Gérez tout votre business depuis un seul endroit',
      icon: Layout,
      gradient: 'from-violet-500 via-purple-500 to-indigo-600',
      path: '/workspace',
      cta: 'Accéder',
      stat: 'Tout-en-1',
      statLabel: 'facturation, clients, stock...',
    }] : []),
  ], [isAuthor]);

  // Auto-rotation du spotlight
  useEffect(() => {
    if (spotlightPaused || spotlightSlides.length === 0) return;
    const interval = setInterval(() => {
      setSpotlightIndex(prev => (prev + 1) % spotlightSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [spotlightPaused, spotlightSlides.length]);

  // ============================================================
  // ASTUCE DU JOUR
  // ============================================================

  const tipOfTheDay = useMemo(() => {
    const tips = [
      { text: "Utilisez Ctrl+K pour rechercher rapidement n'importe quelle fonctionnalité.", icon: Search, color: 'text-blue-500', path: null, cta: null },
      { text: "Créez votre Vitrine pour présenter vos réalisations et attirer des clients.", icon: Eye, color: 'text-emerald-500', path: '/showcase', cta: 'Ma Vitrine' },
      { text: "Le Workspace regroupe facturation, clients, produits et rapports en un seul endroit.", icon: Layout, color: 'text-violet-500', path: '/workspace', cta: 'Mon Workspace' },
      { text: "Explorez le Feed pour découvrir des opportunités qui correspondent à votre profil.", icon: Sparkles, color: 'text-rose-500', path: '/feed', cta: 'Voir le Feed' },
      { text: "Configurez votre boutique en ligne pour vendre vos produits directement.", icon: Store, color: 'text-pink-500', path: '/workspace/shop', cta: 'Ma Boutique' },
      { text: "Suivez vos finances avec les outils de portefeuille et de gestion des dépenses.", icon: Wallet, color: 'text-amber-500', path: '/workspace/wallet', cta: 'Mon Portefeuille' },
      { text: "Invitez des collaborateurs dans votre équipe pour travailler ensemble.", icon: Users, color: 'text-cyan-500', path: '/workspace/team', cta: 'Mon Équipe' },
      { text: "Consultez le Guide pour maîtriser toutes les fonctionnalités de DJAMANN.", icon: BookOpen, color: 'text-indigo-500', path: '/guide', cta: 'Ouvrir le Guide' },
    ];
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    return tips[dayOfYear % tips.length];
  }, []);

  // ============================================================
  // RÉCEMMENT VISITÉ (mapped to sections)
  // ============================================================

  const recentSections = useMemo(() => {
    if (recentPages.length === 0) return [];
    return recentPages
      .map(path => allSections.find(s => s.path === path))
      .filter(Boolean)
      .slice(0, 5);
  }, [recentPages, allSections]);

  // ============================================================
  // GREETING MESSAGE
  // ============================================================

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <GlobalStyles />

      {/* ===== HERO / WELCOME SECTION ===== */}
      <div className="relative overflow-hidden">
        {/* Background décoratif animé */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl animate-float" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-100/20 via-indigo-100/20 to-purple-100/20 rounded-full blur-3xl animate-pulse-soft" />
          {/* Particules décoratives */}
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-indigo-400/20 rounded-full animate-float"
              style={{
                left: `${15 + i * 18}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 1.2}s`,
                animationDuration: `${5 + i}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 sm:pt-12 sm:pb-8">
          {/* Salutation avec animation d'entrée */}
          <div className={`mb-8 sm:mb-10 transition-all duration-700 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 animate-gradient-x flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg shadow-indigo-200 hover:scale-110 transition-transform duration-300 cursor-pointer"
                   onClick={() => navigate('/profile')}>
                {user?.nom?.charAt(0)?.toUpperCase() || 'D'}
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                  {getGreeting()}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 animate-gradient-x">{user?.nom || 'Utilisateur'}</span>
                </h1>
                <p className="text-sm sm:text-base text-gray-500 mt-0.5">
                  {isAuthor ? '👨‍💼 Espace Professionnel' : '👋 Bienvenue sur DJAMANN'}
                  {isAdmin && ' • 🛡️ Administrateur'}
                  <span className="hidden sm:inline text-gray-400 ml-2">— {formattedDate}</span>
                </p>
              </div>
            </div>
          </div>



          {/* ===== ACTIONS RAPIDES ANIMÉES ===== */}
          <div className={`mb-2 transition-all duration-700 ease-out delay-200 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-amber-500 animate-bounce-subtle" />
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Accès rapide</h2>
            </div>
            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleNavigate(action.path)}
                  className={`flex-shrink-0 group group-shimmer flex items-center gap-2 sm:gap-2.5 px-4 py-2.5 sm:px-5 sm:py-3 bg-white rounded-xl border border-gray-200 hover:border-transparent hover:shadow-lg hover:shadow-gray-200/50 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden`}
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  {/* Shimmer overlay */}
                  <div className="shimmer-effect absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-0 group-hover:opacity-100" />
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                    <action.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap relative z-10">{action.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all duration-300" />
                </button>
              ))}
            </div>
          </div>

          {/* ===== ASTUCE DU JOUR ===== */}
          {!tipDismissed && (
            <div className={`mt-6 transition-all duration-700 ease-out delay-[250ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-amber-700">Astuce du jour : </span>
                    {tipOfTheDay.text}
                  </p>
                </div>
                {tipOfTheDay.path && (
                  <button
                    onClick={() => handleNavigate(tipOfTheDay.path)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-all duration-200 hover:scale-105 shadow-sm"
                  >
                    {tipOfTheDay.cta}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => setTipDismissed(true)} className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-amber-100/50 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ===== RÉCEMMENT VISITÉ ===== */}
          {recentSections.length > 0 && (
            <div className={`mt-5 transition-all duration-700 ease-out delay-[300ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Récemment visité</h2>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {recentSections.map((section) => {
                  const RecentIcon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleNavigate(section.path)}
                      className="flex-shrink-0 group flex items-center gap-2 px-3 py-2 bg-white/80 hover:bg-white rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                    >
                      <div className={`w-6 h-6 rounded-md ${section.bgLight} flex items-center justify-center`}>
                        <RecentIcon className={`w-3.5 h-3.5 ${section.iconColor}`} />
                      </div>
                      <span className="text-xs font-medium text-gray-600 group-hover:text-gray-900 whitespace-nowrap">{section.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ===== SPOTLIGHT SLIDER ===== */}
      {!searchTerm && (
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 transition-all duration-700 ease-out delay-300 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden"
            onMouseEnter={() => setSpotlightPaused(true)}
            onMouseLeave={() => setSpotlightPaused(false)}
          >
            {/* Slides */}
            <div className="relative h-64 sm:h-72 md:h-80">
              {spotlightSlides.map((slide, idx) => {
                const SlideIcon = slide.icon;
                const isActive = idx === spotlightIndex;
                return (
                  <div
                    key={slide.id}
                    className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} transition-all duration-700 ease-in-out ${
                      isActive
                        ? 'opacity-100 scale-100 z-10'
                        : 'opacity-0 scale-105 z-0 pointer-events-none'
                    }`}
                  >
                    {/* Pattern animé */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                        backgroundSize: '32px 32px',
                      }} />
                    </div>

                    {/* Cercles décoratifs animés */}
                    <div className="absolute -right-16 -top-16 w-64 h-64 bg-white/10 rounded-full blur-2xl animate-pulse-soft" />
                    <div className="absolute -left-8 -bottom-8 w-48 h-48 bg-white/5 rounded-full blur-xl animate-float" />

                    {/* Contenu */}
                    <div className="relative h-full flex items-center px-6 sm:px-10 md:px-12 pb-14 z-10">
                      <div className="flex-1 max-w-lg">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-md rounded-full mb-3 sm:mb-4 transition-all duration-700 ${
                          isActive ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
                        }`} style={{ transitionDelay: '200ms' }}>
                          <SlideIcon className="w-4 h-4 text-white" />
                          <span className="text-xs font-medium text-white/90">{slide.stat} {slide.statLabel}</span>
                        </div>

                        <h3 className={`text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3 transition-all duration-700 ${
                          isActive ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
                        }`} style={{ transitionDelay: '100ms' }}>
                          {slide.title}
                        </h3>

                        <p className={`text-sm sm:text-base text-white/80 mb-4 sm:mb-5 max-w-md transition-all duration-700 ${
                          isActive ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
                        }`} style={{ transitionDelay: '200ms' }}>
                          {slide.subtitle}
                        </p>

                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(slide.path); }}
                          className={`relative z-30 group/cta inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white font-semibold text-sm rounded-xl border border-white/25 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer ${
                            isActive ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                          }`}
                          style={{ transitionDelay: '300ms' }}
                        >
                          {slide.cta}
                          <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                        </button>
                      </div>

                      {/* Grande icône décorative à droite */}
                      <div className="hidden md:flex items-center justify-center">
                        <div className={`relative transition-all duration-700 ${
                          isActive ? 'scale-100 rotate-0 opacity-100' : 'scale-75 rotate-12 opacity-0'
                        }`}>
                          <div className="w-32 h-32 lg:w-40 lg:h-40 bg-white/10 rounded-3xl backdrop-blur-sm flex items-center justify-center rotate-6 hover:rotate-0 transition-transform duration-500">
                            <SlideIcon className="w-16 h-16 lg:w-20 lg:h-20 text-white/80" strokeWidth={1} />
                          </div>
                          <div className="absolute -inset-4 bg-white/5 rounded-3xl blur-xl animate-pulse-soft" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Contrôles du slider */}
            <div className="absolute bottom-4 left-6 sm:left-10 right-6 sm:right-10 flex items-center justify-between z-20">
              {/* Indicateurs de progression */}
              <div className="flex items-center gap-2">
                {spotlightSlides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSpotlightIndex(idx)}
                    className="relative h-1.5 rounded-full overflow-hidden transition-all duration-300"
                    style={{ width: idx === spotlightIndex ? '2rem' : '0.5rem' }}
                  >
                    <div className="absolute inset-0 bg-white/30 rounded-full" />
                    {idx === spotlightIndex && (
                      <div
                        className="absolute inset-0 bg-white rounded-full"
                        style={{
                          animation: spotlightPaused ? 'none' : 'progress-bar 5s linear',
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Boutons prev/next + pause */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setSpotlightPaused(!spotlightPaused)}
                  className="p-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-lg transition-colors"
                >
                  {spotlightPaused
                    ? <Play className="w-3.5 h-3.5 text-white" />
                    : <Pause className="w-3.5 h-3.5 text-white" />
                  }
                </button>
                <button
                  onClick={() => setSpotlightIndex(spotlightIndex === 0 ? spotlightSlides.length - 1 : spotlightIndex - 1)}
                  className="p-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5 text-white" />
                </button>
                <button
                  onClick={() => setSpotlightIndex((spotlightIndex + 1) % spotlightSlides.length)}
                  className="p-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-lg transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ANNONCES ADMINISTRATEUR ===== */}
      {user && announcements.filter(a => !dismissedAnnouncements.includes(a.id)).length > 0 && (
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 transition-all duration-700 ease-out delay-[350ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-2 mb-3">
            <Megaphone className="w-4 h-4 text-indigo-500" />
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Annonces</h2>
          </div>
          <div className="space-y-3">
            {announcements.filter(a => !dismissedAnnouncements.includes(a.id)).map((ann) => (
              <div key={ann.id} className="relative bg-gradient-to-r from-indigo-50 via-blue-50 to-purple-50 border border-indigo-200/60 rounded-xl p-4 sm:p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                    <Megaphone className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{ann.titre}</h3>
                        {ann.author_name && (
                          <p className="text-xs text-indigo-500 mt-0.5">De : {ann.author_name}</p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          try { await announcementAPI.markAsRead(ann.id); } catch {}
                          const updated = [...dismissedAnnouncements, ann.id];
                          setDismissedAnnouncements(updated);
                          localStorage.setItem('djamann_dismissed_announcements', JSON.stringify(updated));
                        }}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-indigo-100/50 transition-colors"
                        title="Marquer comme lu"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-1.5 leading-relaxed whitespace-pre-wrap line-clamp-3">{ann.message}</p>
                    {ann.created_at && (
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(ann.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== BANNIÈRES PROMOTIONNELLES ===== */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 transition-all duration-700 ease-out delay-[400ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Bannière Workspace — pour les auteurs */}
        {isAuthor && (
          <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-2xl p-5 sm:p-6 shadow-xl relative overflow-hidden mb-4">
            {/* Pattern décoratif */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '24px 24px',
              }} />
            </div>
            <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-2xl animate-pulse-soft" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl flex-shrink-0 shadow-inner">
                  🏢
                </div>
                <div className="text-white">
                  <h3 className="text-lg sm:text-xl font-bold">Mon Espace Business</h3>
                  <p className="text-white/80 text-sm">Gérez vos clients, factures, devis et comptabilité</p>
                </div>
              </div>
              <button
                onClick={() => handleNavigate('/workspace')}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-purple-700 font-semibold text-sm rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex-shrink-0"
              >
                Accéder au Workspace
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Bannière Devenir Auteur — pour les utilisateurs réguliers */}
        {user && !isAuthor && (
          <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border border-amber-200 rounded-2xl p-5 sm:p-6 shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="text-4xl flex-shrink-0">🚀</div>
                <div>
                  <h3 className="text-lg font-bold text-amber-800">Vous avez un projet en tête ?</h3>
                  <p className="text-amber-700 text-sm">
                    Devenez <span className="font-semibold">Auteur</span> et créez vos propres projets, recrutez des talents, ouvrez votre boutique !
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleNavigate('/my-subscription')}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex-shrink-0"
              >
                Devenir Auteur
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== CONTENU PRINCIPAL ===== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">

        {/* ===== ONGLETS CATÉGORIES + RECHERCHE ===== */}
        <div className="mb-8 sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/80 backdrop-blur-xl border-b border-gray-100">
          {/* Barre de recherche */}
          <form onSubmit={handleSearchSubmit} className="mb-3">
            <div className="relative">
              <div className={`
                relative flex items-center bg-white rounded-xl border transition-all duration-300 shadow-sm overflow-hidden
                ${isSearchFocused 
                  ? 'border-indigo-400 shadow-md shadow-indigo-100' 
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}>
                <Search className={`ml-3 w-4 h-4 transition-all duration-300 ${isSearchFocused ? 'text-indigo-500 scale-110' : 'text-gray-400'}`} />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Rechercher une fonctionnalité... (Ctrl+K)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className="flex-1 px-3 py-2.5 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-sm relative z-10"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="mr-2 px-2 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors relative z-10"
                  >
                    Effacer
                  </button>
                )}
                <div className="hidden sm:flex items-center mr-3 px-1.5 py-0.5 bg-gray-100 rounded-md relative z-10">
                  <span className="text-[10px] text-gray-400 font-mono">Ctrl+K</span>
                </div>
              </div>
              {searchTerm && (
                <p className="mt-1.5 ml-1 text-xs text-gray-500">
                  {filteredSections.length} résultat{filteredSections.length !== 1 ? 's' : ''} trouvé{filteredSections.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </form>
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`
                    relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm font-medium whitespace-nowrap 
                    transition-all duration-300 ease-out
                    ${isActive
                      ? `${cat.bg} ${cat.color} shadow-sm scale-105`
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:scale-[1.02]'
                    }
                  `}
                >
                  <cat.icon className={`w-4 h-4 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                  <span>{cat.label}</span>
                  {isActive && !searchTerm && (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-white/70 animate-fade-in-scale">
                      {activeCategory === 'all' ? allSections.length : allSections.filter(s => s.category === cat.id).length}
                    </span>
                  )}
                  {/* Indicateur actif animé */}
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-current animate-fade-in-scale" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ===== GRILLE DES SECTIONS ===== */}
        {filteredSections.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {filteredSections.map((section, index) => (
              <SectionCard
                key={section.id}
                section={section}
                index={index}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        ) : (
          /* Aucun résultat */
          <div className="text-center py-16 sm:py-24">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gray-100 flex items-center justify-center">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun résultat</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Aucune fonctionnalité ne correspond à « {searchTerm} ». Essayez avec d'autres termes.
            </p>
            <button
              onClick={() => { setSearchTerm(''); setActiveCategory('all'); }}
              className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        )}

        {/* ===== SECTION "POUR COMMENCER" avec animations ===== */}
        {!searchTerm && activeCategory === 'all' && (
          <CtaSection navigate={navigate} isAuthor={isAuthor} />
        )}
      </div>

      {/* ===== FOOTER COMPACT ===== */}
      <footer className="border-t border-gray-100 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-xs font-bold">D</span>
              </div>
              <span className="text-sm font-semibold text-gray-700">DJAMANN</span>
              <span className="text-xs text-gray-400">— Tout-en-un pour les pros</span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <button onClick={() => navigate('/guide')} className="text-xs text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" />
                Guide
              </button>
              <button onClick={() => navigate('/settings')} className="text-xs text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1">
                <Settings className="w-3.5 h-3.5" />
                Paramètres
              </button>
              <button onClick={() => navigate('/profile')} className="text-xs text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                Profil
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============================================================
// COMPOSANT CARTE DE SECTION (avec animations améliorées)
// ============================================================

// eslint-disable-next-line react/prop-types
const SectionCard = React.memo(({ section, index, onNavigate }) => {
  const [ref, setRef] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!ref) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref]);

  const IconComponent = section.icon;

  return (
    <button
      ref={setRef}
      onClick={() => onNavigate(section.path)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        group relative text-left w-full bg-white rounded-2xl border border-gray-100 
        p-5 sm:p-6 transition-all duration-500 ease-out overflow-hidden
        hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-200 hover:-translate-y-1.5
        focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300
        ${isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-6'
        }
        ${section.featured ? 'ring-2 ring-indigo-100' : ''}
      `}
      style={{ transitionDelay: `${Math.min(index * 60, 350)}ms` }}
    >
      {/* Gradient background on hover */}
      <div className={`
        absolute inset-0 bg-gradient-to-br ${section.gradient} transition-opacity duration-500 rounded-2xl
        ${isHovered ? 'opacity-[0.04]' : 'opacity-0'}
      `} />

      {/* Shimmer effect on hover */}
      <div className={`
        absolute inset-0 overflow-hidden rounded-2xl pointer-events-none
        ${isHovered ? 'opacity-100' : 'opacity-0'}
      `}>
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          style={{
            animation: isHovered ? 'shimmer 1s ease-in-out' : 'none',
          }}
        />
      </div>

      {/* Featured badge avec animation */}
      {section.featured && (
        <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[10px] font-bold rounded-full shadow-md animate-bounce-subtle">
          ★ PRO
        </div>
      )}
      
      {/* Icône avec animation de rotation au hover */}
      <div className={`
        relative w-12 h-12 rounded-xl ${section.bgLight} 
        flex items-center justify-center mb-4
        transition-all duration-500 ease-out
        ${isHovered ? 'scale-110 rotate-3 shadow-lg' : 'scale-100 rotate-0'}
      `}>
        <IconComponent className={`w-6 h-6 ${section.iconColor} transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`} />
        {/* Halo effect */}
        <div className={`absolute inset-0 rounded-xl ${section.bgLight} blur-md transition-opacity duration-500 ${isHovered ? 'opacity-60' : 'opacity-0'}`} />
      </div>

      {/* Contenu avec translation au hover */}
      <h3 className={`font-semibold text-gray-900 mb-1.5 text-base transition-all duration-300 ${isHovered ? 'text-indigo-700 translate-x-0.5' : ''}`}>
        {section.title}
      </h3>
      <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 transition-colors duration-300 group-hover:text-gray-600">
        {section.description}
      </p>

      {/* Flèche d'action animée */}
      <div className={`mt-4 flex items-center text-sm font-medium text-indigo-600 transition-all duration-400 ${isHovered ? 'opacity-100 translate-x-1' : 'opacity-0 translate-x-0'}`}>
        <span>Accéder</span>
        <ArrowRight className={`w-4 h-4 ml-1 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
      </div>

      {/* Barre de couleur en bas avec expansion animée */}
      <div className={`absolute bottom-0 left-1/2 h-0.5 bg-gradient-to-r ${section.gradient} rounded-full transition-all duration-500 ${isHovered ? 'opacity-100 w-3/4 -translate-x-1/2' : 'opacity-0 w-0 -translate-x-1/2'}`} />
    </button>
  );
});

SectionCard.displayName = 'SectionCard';

// ============================================================
// COMPOSANT CTA SECTION (avec scroll animation)
// ============================================================

// eslint-disable-next-line react/prop-types
const CtaSection = React.memo(({ navigate, isAuthor }) => {
  const [ref, setRef] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref]);

  const steps = useMemo(() => [
    {
      step: '1',
      title: 'Complétez votre profil',
      desc: 'Un profil complet attire plus d\'opportunités',
      action: () => navigate('/profile'),
      cta: 'Mon Profil',
      icon: User,
    },
    {
      step: '2',
      title: 'Explorez les opportunités',
      desc: 'Découvrez des projets qui vous correspondent',
      action: () => navigate('/feed'),
      cta: 'Voir le Feed',
      icon: Sparkles,
    },
    {
      step: '3',
      title: isAuthor ? 'Lancez votre Workspace' : 'Parcourez les services',
      desc: isAuthor ? 'Gérez votre activité de A à Z' : 'Trouvez le professionnel idéal',
      action: () => navigate(isAuthor ? '/workspace' : '/services'),
      cta: isAuthor ? 'Mon Workspace' : 'Services',
      icon: isAuthor ? Layout : Briefcase,
    },
  ], [isAuthor, navigate]);

  return (
    <div ref={setRef} className="mt-16">
      <div className={`
        bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-8 sm:p-10 lg:p-12 
        relative overflow-hidden transition-all duration-1000 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
      `}>
        {/* Déco animée */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse-soft" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-float" />

        <div className="relative z-10">
          <div className={`flex items-center gap-3 mb-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center animate-bounce-subtle">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Explorez DJAMANN</h2>
          </div>
          <p className={`text-white/80 text-sm sm:text-base mb-8 max-w-2xl transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            DJAMANN regroupe tout ce dont vous avez besoin : opportunités, gestion de projets, 
            facturation, marketplace, boutique en ligne, et bien plus. Voici comment bien démarrer :
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {steps.map((item, idx) => {
              const StepIcon = item.icon;
              return (
                <button
                  key={idx}
                  onClick={item.action}
                  className={`
                    group text-left bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-2xl p-5 
                    border border-white/10 hover:border-white/30 transition-all duration-500 
                    hover:-translate-y-1 hover:shadow-lg hover:shadow-black/10
                    ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                  `}
                  style={{ transitionDelay: `${400 + idx * 150}ms` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm group-hover:scale-110 group-hover:bg-white/30 transition-all duration-300">
                      {item.step}
                    </div>
                    <h3 className="font-semibold text-white text-sm sm:text-base">{item.title}</h3>
                  </div>
                  <p className="text-white/70 text-xs sm:text-sm mb-4">{item.desc}</p>
                  <div className="flex items-center gap-1.5 text-white/90 text-xs font-medium group-hover:gap-2.5 transition-all">
                    <StepIcon className="w-3.5 h-3.5 opacity-70" />
                    <span>{item.cta}</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

CtaSection.displayName = 'CtaSection';

export default HomePage;

